import type { AuthTokensDto } from '@jalupro/shared';

/**
 * Typed fetch client for the Jalupro API.
 *
 * - Base URL from `VITE_API_URL` (default `/api`, single-host path routing).
 * - Bearer access token attached automatically; on a 401 it transparently tries
 *   the refresh token once, then replays the request. If refresh fails it clears
 *   the session and surfaces an `ApiError` so the auth layer can bounce to /login.
 * - Tokens live in localStorage (access + refresh). PCI/card data never touches us.
 */

const API_BASE = (import.meta.env.VITE_API_URL ?? '/api').replace(/\/$/, '');

const ACCESS_KEY = 'jalupro.accessToken';
const REFRESH_KEY = 'jalupro.refreshToken';

/** Error thrown for non-2xx responses (and network failures). */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

// ── Token store ──────────────────────────────────────────────────────────────
export const tokenStore = {
  get access(): string | null {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh(): string | null {
    return localStorage.getItem(REFRESH_KEY);
  },
  set(tokens: AuthTokensDto): void {
    localStorage.setItem(ACCESS_KEY, tokens.accessToken);
    localStorage.setItem(REFRESH_KEY, tokens.refreshToken);
  },
  clear(): void {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
  get hasSession(): boolean {
    return Boolean(localStorage.getItem(ACCESS_KEY));
  },
};

/** Subscribers notified when the session is cleared (e.g. failed refresh). */
type SessionListener = () => void;
const sessionListeners = new Set<SessionListener>();
export function onSessionCleared(listener: SessionListener): () => void {
  sessionListeners.add(listener);
  return () => sessionListeners.delete(listener);
}
function clearSession(): void {
  tokenStore.clear();
  sessionListeners.forEach((l) => l());
}

// ── Core request ─────────────────────────────────────────────────────────────
export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /** JSON body — serialized automatically. */
  body?: unknown;
  /** Query-string params (undefined/null values are skipped). */
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Skip the Authorization header (used for login/refresh). */
  anonymous?: boolean;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null) qs.append(k, String(v));
  }
  const s = qs.toString();
  return s ? `${url}?${s}` : url;
}

let refreshInFlight: Promise<boolean> | null = null;

async function attemptRefresh(): Promise<boolean> {
  const refreshToken = tokenStore.refresh;
  if (!refreshToken) return false;
  // De-dupe concurrent refreshes.
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await fetch(buildUrl('/auth/refresh'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
        if (!res.ok) return false;
        const tokens = (await res.json()) as AuthTokensDto;
        tokenStore.set(tokens);
        return true;
      } catch {
        return false;
      } finally {
        refreshInFlight = null;
      }
    })();
  }
  return refreshInFlight;
}

async function parseBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return undefined;
  const type = res.headers.get('content-type') ?? '';
  if (type.includes('application/json')) {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }
  return text;
}

function errorMessage(body: unknown, fallback: string): string {
  if (body && typeof body === 'object') {
    const m = (body as Record<string, unknown>).message;
    if (typeof m === 'string') return m;
    if (Array.isArray(m) && m.length) return String(m[0]);
  }
  return fallback;
}

async function rawRequest<T>(path: string, options: RequestOptions, retry = true): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (!options.anonymous && tokenStore.access) {
    headers.Authorization = `Bearer ${tokenStore.access}`;
  }

  let res: Response;
  try {
    res = await fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
    });
  } catch (cause) {
    throw new ApiError(0, 'Network error — could not reach the server.', cause);
  }

  // Transparent single refresh-and-replay on 401.
  if (res.status === 401 && retry && !options.anonymous && tokenStore.refresh) {
    const refreshed = await attemptRefresh();
    if (refreshed) return rawRequest<T>(path, options, false);
    clearSession();
  }

  const body = await parseBody(res);
  if (!res.ok) {
    throw new ApiError(res.status, errorMessage(body, `Request failed (${res.status})`), body);
  }
  return body as T;
}

/** Low-level typed request helpers. Domain methods in ./endpoints build on these. */
export const http = {
  get: <T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    rawRequest<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    rawRequest<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    rawRequest<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    rawRequest<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    rawRequest<T>(path, { ...options, method: 'DELETE' }),
};

export { API_BASE };
